// Homepage hero — pixel-for-pixel parity with staging (/newsite/).
// Staging CSS source: post-724.css > .elementor-element-527cfe7 (container)
//                     and .elementor-element-6572468 (h1).
// Desktop background: /wp-content/uploads/2025/10/Frame-10-1.png (top-center, cover)
// Mobile background:  /wp-content/uploads/2025/11/Group-48096136-1-e1763207692904.png (center)
// H1: "Futura PT Heavy" 48px/1.2em, weight 500, uppercase, white
// Mobile H1: 23px
// Container min-height: 95vh desktop / 80vh mobile; padding: 3% 8% (desktop).
// Background-color fill behind image: #231F20 (charcoal).
import { useRequestInfo } from "../forms/RequestInfoModal";

const HERO_DESKTOP = "/assets/home/hero-desktop.png";
const HERO_MOBILE = "/assets/home/hero-mobile.png";

const Hero = () => {
  // Book Appointment opens the same Request Information modal used by every
  // other lead-capture CTA on the site (same fields, same /api/enquiry
  // workflow, same thank-you redirect). Tagged formType="book_appointment"
  // so CRM/Rivery can segment source attribution without changing the flow.
  const { open: openRequestInfo } = useRequestInfo();
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative w-full overflow-hidden ac-hero"
      style={{ backgroundColor: "#231F20" }}
    >
      {/* Responsive background images via <picture> for mobile-desktop swap.
          WebP variants (~30-50KB each) load on modern browsers; PNG fallback
          for ancient browsers. LCP image is preloaded in index.html. */}
      <picture>
        <source media="(max-width: 767.98px)" srcSet="/assets/home/hero-mobile.webp" type="image/webp" />
        <source media="(max-width: 767.98px)" srcSet={HERO_MOBILE} />
        <source srcSet="/assets/home/hero-desktop.webp" type="image/webp" />
        <img
          src={HERO_DESKTOP}
          alt="Anderson College nursing graduate smiling in uniform on campus — Toronto, Ontario"
          /* Explicit intrinsic dimensions (2026-02 CLS fix). The parent
             <section> already enforces a fixed min-height via Tailwind
             classes, so these attributes change nothing visually; they
             exist so Lighthouse / Core Web Vitals stop flagging "image
             elements do not have explicit width and height" and so a
             user on a slow connection with CSS still loading sees the
             browser reserve the correct aspect ratio immediately.

             2026-10: removed aria-hidden + decorative alt="". The hero
             photo IS informational (graduate on campus) and now gets a
             descriptive alt text so Google Images + screen-reader users
             have context. */
          width="2400"
          height="1200"
          className="absolute inset-0 w-full h-full object-cover ac-hero-img"
          style={{ objectPosition: "top center" }}
          loading="eager"
          fetchPriority="high"
          data-testid="hero-bg-image"
        />
      </picture>

      {/* 2026-06-01: Uniform hero treatment — matches the program-page
          settings (per client request to standardise across the site).
          Two layers:
            1. A flat black tint (~0.38 opacity) to soften busy photo detail.
            2. A diagonal left-side scrim to guarantee WCAG-AA contrast for
               the white headline at any breakpoint.
          2026-02 update: desktop diagonal scrim peak reduced to 0.55 per
          product brief so the photograph reads brighter on wide screens. */}
      {/* 2026-06-02: Homepage hero tint unified with ProgramTemplate +
          SubpageHero so every page across the site shares the same dark
          scrim. (Solid tint = 0.38; desktop scrim = 0.78→0; mobile
          bottom-fade = 0.0→0.50.) Previously the homepage was 0.22 / 0.32
          which created a visible brightness step between the homepage
          and every subpage. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundColor: "rgba(53,53,53,0.38)", zIndex: 1 }}
      />
      <div
        aria-hidden="true"
        className="hidden md:block absolute inset-y-0 left-0 w-3/5 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(15,15,15,0.78) 0%, rgba(15,15,15,0.55) 45%, rgba(15,15,15,0.20) 75%, rgba(15,15,15,0) 100%)",
          zIndex: 1,
        }}
      />
      {/* Mobile bottom-fade scrim — matches the program-page mobile treatment
          so the CTA + headline stay readable when the navbar is transparent. */}
      <div
        aria-hidden="true"
        className="md:hidden absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,15,15,0) 0%, rgba(15,15,15,0) 55%, rgba(15,15,15,0.32) 85%, rgba(15,15,15,0.50) 100%)",
          zIndex: 1,
        }}
      />

      {/* Content — anchored to bottom-left on desktop (per 2026-04-29 brief).
          On mobile we flip to top-anchored so the motto fills the empty
          upper area and the bottom doesn't feel crowded with the CTA. */}
      <div className="ac-hero-inner relative z-10 w-full flex items-end ac-hero-items">
        <div className="ac-container w-full">
          {/* Inner column — 62% width on desktop per staging */}
          <div className="ac-hero-col">
            <h1
              className="ac-hero-h1"
              data-testid="hero-heading"
              style={{
                fontFamily: '"Futura PT Heavy", var(--font-display), Sans-serif',
                fontSize: "48px",
                fontWeight: 500,
                lineHeight: "1.2em",
                color: "#FFFFFF",
                textTransform: "uppercase",
                margin: 0,
              }}
            >
              <span className="home-banner">
                You Have What It<br /> Takes to Succeed.<br /> We Just Prove that{" "}
                <br />
                <span className="ac-hero-underline">You Can!</span>
              </span>
            </h1>

            <div style={{ paddingTop: "30px" }}>
              <button
                type="button"
                onClick={() => openRequestInfo({ formType: "book_appointment" })}
                className="ac-hero-cta"
                data-testid="hero-book-appointment"
              >
                Book Appointment
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .ac-hero { position: relative; }
        .ac-hero-inner { min-height: 95vh; padding: 3% 0 5% 0; }
        /* 2026-02 product brief: zoom the background photo slightly so the
           subject reads larger on widescreen monitors. We scale the <img>
           element (not the container) so the absolutely-positioned scrim
           layers above remain unaffected. */
        .ac-hero-img {
          transform: scale(1.12);
          transform-origin: center top;
          transition: transform 0.4s ease;
        }
        @media (max-width: 767.98px) {
          .ac-hero-img { transform: scale(1.06); transform-origin: 70% 22%; }
        }
        /* Desktop column width matches staging 62% */
        .ac-hero-col { width: 62%; max-width: 62%; }
        /* Decorative gold accent under "You Can!" — replaces the previous
           text-decoration: underline which read as a hyperlink (UX audit
           2026-05-29). Sits inline so the breakline math stays unchanged. */
        .ac-hero-underline {
          position: relative;
          display: inline-block;
          padding-bottom: 6px;
        }
        .ac-hero-underline::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 4px;
          background: #f5c056;       /* brand gold accent */
          border-radius: 2px;
        }
        /* Red CTA — staging button: #A21D23, Futura PT Medium 14px/500, uppercase, 15px/52px padding, radius 10 */
        .ac-hero-cta {
          display: inline-block;
          background-color: #A21D23;
          color: #FFFFFF;
          font-family: "Futura PT Medium", var(--font-display), Sans-serif;
          font-size: 14px;
          font-weight: 500;
          text-transform: uppercase;
          border-radius: 10px;
          padding: 15px 52px;
          letter-spacing: 0;
          transition: background-color 0.2s ease;
          text-decoration: none;
          border: 0;
          cursor: pointer;
        }
        .ac-hero-cta:hover { background-color: #1D426C; }

        /* Mobile — ≤767px. Copy + CTA pinned to BOTTOM-LEFT (per user
           feedback 2026-05-22) so the graduate's face is no longer
           obscured by the headline. The hero background is re-cropped
           (object-position 65% 25%) to keep the subject in the upper-
           right area of the frame, well clear of the text column. */
        @media (max-width: 767.98px) {
          .ac-hero-items {
            align-items: flex-end !important;
          }
          .ac-hero-inner {
            min-height: 88vh;
            padding: 0 0 28px 0;
          }
          .ac-hero-col {
            /* Hold the text to a left-aligned column ~70% of the screen
               width so a small slab at the bottom-left carries the copy
               and the rest of the photo breathes. */
            width: 100%;
            max-width: 100%;
            text-align: left;
          }
          .ac-hero-h1 {
            font-size: 22px !important;
            line-height: 1.15 !important;
            max-width: 78% !important;
          }
          /* Shift the subject toward the upper-right so the lower-left
             quadrant — where the headline + CTA sit — is mostly
             background / softer detail. */
          [data-testid="hero-bg-image"] {
            object-position: 70% 22% !important;
          }
          /* Bottom-left readability scrim — protects white text against
             whatever pixel-detail ends up under it without darkening the
             whole image (the original full-screen tint felt too heavy). */
          .ac-hero-inner::after {
            content: "";
            position: absolute;
            left: 0;
            bottom: 0;
            width: 100%;
            height: 55%;
            pointer-events: none;
            background: linear-gradient(
              180deg,
              rgba(15, 15, 15, 0)   0%,
              rgba(15, 15, 15, 0.10) 35%,
              rgba(15, 15, 15, 0.55) 75%,
              rgba(15, 15, 15, 0.78) 100%
            );
            z-index: 1;
          }
          /* Force the actual copy + CTA container above the scrim. */
          .ac-hero-inner > .ac-container {
            position: relative;
            z-index: 2;
          }
          /* Compact BOOK APPOINTMENT CTA on mobile — inline auto-width
             instead of full-bleed block (per user feedback 2026-05-21). */
          .ac-hero-cta {
            display: inline-block !important;
            width: auto !important;
            padding: 12px 28px !important;
            font-size: 13px !important;
            letter-spacing: 0.08em !important;
          }
        }

        /* Tablet (iPad portrait 768–1023) — pre-audit min-height of 95vh
           pushed the headline far below the fold (audit finding 2026-05-29).
           Reduce to 78vh so the headline + CTA land above the fold at
           820×1180. Bounded to 1023.98 to avoid 1px overlap with lg (1024). */
        @media (min-width: 768px) and (max-width: 1023.98px) {
          .ac-hero-inner {
            min-height: 78vh;
            padding: 4% 0 6% 0;
          }
          .ac-hero-col {
            width: 70%;
            max-width: 70%;
          }
          .ac-hero-h1 {
            font-size: 38px !important;
          }
          /* Bottom safety padding below the CTA so it never hugs the edge */
          .ac-hero-cta {
            margin-bottom: 8px;
          }
        }

        /* Tablet 1024–1366 same font-size retained per staging CSS (48px) */
      `}</style>
    </section>
  );
};

export default Hero;
